import { c as _c } from "react/compiler-runtime";
import { ReactNode, useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import { Input } from '@/comps/primitives/input';
import { FilterContentV2Pill, FilterContentV2PillClassNames } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2Pill';
import { FilterContentV2FieldDropdown, FilterContentV2FieldType } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2FieldDropdown';
import { FilterContentV2OperatorDropdown } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2OperatorDropdown';
import { FilterContentV2ValueDropdown } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2ValueDropdown';
import { FilterContentV2SpecialTimeKindDropdown } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2SpecialTimeKindDropdown';
import { FilterContentV2SpecialTimeValuePopover } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2SpecialTimeValuePopover';
import { getSpecialTimeKindLabel, SpecialTimeFilterState, SpecialTimeKind } from '@/comps/FilterPicker/filter-content-v2/specialTimeFilterAdapter';
import { FilterContentV2InteractionSpecialKindDropdown } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2InteractionSpecialKindDropdown';
import { FilterContentV2InteractionSpecialValuePopover } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2InteractionSpecialValuePopover';
import { FilterContentV2UserMetricSpecialValuePopover } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2UserMetricSpecialValuePopover';
import { getValueComparatorLabel, InteractionSpecialFilterState, ValueComparatorKind } from '@/comps/FilterPicker/filter-content-v2/interactionSpecialFilterAdapter';
import { UserMetricSpecialFilterState } from '@/comps/FilterPicker/filter-content-v2/userMetricSpecialFilterAdapter';
import { FilterContentV2LastInteractionTagValuePopover } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2LastInteractionTagValuePopover';
import { LastInteractionTagFilterState } from '@/comps/FilterPicker/filter-content-v2/lastInteractionTagFilterAdapter';
import { FilterOperator, getOperatorLabel, hasSingleOperatorChoice } from '@/comps/FilterPicker/filter-operators';
import { FILTER_CONTENT_V2_POPOVER_CONTENT_CLASS, stopPopoverWheelPropagation } from '@/comps/FilterPicker/filter-content-v2/constants';
import { KindAliasFn } from '@/contexts/alias';
import { buildFilterKindName, formatValue } from '@/comps/FilterPicker/utils';
import { AvailableValue, GenericFilter, VolumeKind } from '@/comps/FilterPicker/FilterPicker';
import { QueryOrdering, FilterGroup, AboveValueConfigKind, InTheLastValueConfigKind, TimeRangeConfig } from '@/api/generated/model';
import { CheckedState } from '@radix-ui/react-checkbox';
import { Button } from '@/comps/primitives/button';
import { cn } from '@/utils/shad';
type DraftPopover = 'field' | 'operator' | 'value' | null;
function focusContainsInput(inputRef: RefObject<HTMLInputElement | null>) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  });
}
const PILL_LABEL_CLASS_NAMES = 'text-xs font-semibold text-white';
const PILL_CHEVRON_CLASS_NAMES = 'text-white';
function formatValuesForPillDisplay(fieldKind: string, values: string[]): string {
  return values.map(value => {
    const formatted = formatValue(fieldKind, value);
    return typeof formatted === 'string' ? formatted : value;
  }).join(', ');
}
function mergePillClassNames(defaults: FilterContentV2PillClassNames, overrides?: FilterContentV2PillClassNames): FilterContentV2PillClassNames {
  return {
    label: cn(defaults.label, overrides?.label),
    button: cn(defaults.button, overrides?.button),
    chevron: cn(defaults.chevron, overrides?.chevron)
  };
}
export type FilterContentV2RowProps = {
  fieldKind: string | null;
  operator: FilterOperator | null;
  fieldLabel?: string;
  valueLabel?: string;
  containsText: string;
  onContainsTextChange: (text: string) => void;
  onApplyContains: (textOverride?: string, columnOverride?: string | null) => void;
  onSelectField: (kind: string) => void;
  onSelectOperator: (operator: FilterOperator) => void;
  onRemove: () => void;
  removeDisabled?: boolean;
  fieldKinds: string[];
  favoriteTypes: Set<string>;
  onToggleFavorite: (kind: string, isFavorite: boolean) => void;
  filterSearch: string;
  setFilterSearch: (v: string) => void;
  sortFavoritesFirst?: (kinds: string[]) => string[];
  selectedFieldType: FilterContentV2FieldType;
  availableFieldTypes: FilterContentV2FieldType[];
  onSelectFieldType: (type: FilterContentV2FieldType) => void;
  fieldListPrepend?: ReactNode;
  savedFilterGroups?: FilterGroup[];
  onSelectSavedFilterGroup?: (filterGroup: FilterGroup) => void;
  onDeleteSavedFilterGroup?: (filterGroup: FilterGroup) => void;
  favoriteSavedFilterGroups?: Set<string>;
  onToggleFavoriteSavedFilterGroup?: (filterGroup: FilterGroup, isFavorite: boolean) => void;
  searchFilterValue: string;
  setSearchFilterValue: (v: string) => void;
  sortBy: QueryOrdering;
  onChangeSortBy: (by: QueryOrdering['by']) => void;
  onChangeSortByDirection: (by: QueryOrdering['by']) => void;
  availableFilterValues?: AvailableValue[];
  isLoadingValues: boolean;
  targetFilterValues: string[];
  hasSelectedAll: boolean;
  showSelectAll: boolean;
  onCheckSelectAll: (checked: CheckedState) => void;
  onSelectFilterValue: (value: AvailableValue, checked: CheckedState) => void;
  volumeKind?: VolumeKind;
  defaultOpenPopover?: DraftPopover;
  onOpenField?: () => void;
  onOpenOperator?: () => void;
  onOpenValue?: () => void;
  openPopoverRequest?: {
    target: DraftPopover;
    nonce: number;
  };
  fieldPillClassNames?: FilterContentV2PillClassNames;
  operatorPillClassNames?: FilterContentV2PillClassNames;
  valuePillClassNames?: FilterContentV2PillClassNames;
  isSpecialTimeFilter?: boolean;
  specialTimeKind?: SpecialTimeKind | null;
  specialTimeState?: SpecialTimeFilterState;
  onSelectSpecialTimeKind?: (kind: SpecialTimeKind) => void;
  onCommitSpecialTimeState?: (state: SpecialTimeFilterState) => void;
  isInteractionSpecialFilter?: boolean;
  interactionSpecialComparatorKind?: ValueComparatorKind | null;
  interactionSpecialState?: InteractionSpecialFilterState;
  onSelectInteractionSpecialComparator?: (kind: ValueComparatorKind) => void;
  onCommitInteractionSpecialState?: (state: InteractionSpecialFilterState) => void;
  isUserMetricSpecialFilter?: boolean;
  userMetricSpecialComparatorKind?: ValueComparatorKind | null;
  userMetricSpecialState?: UserMetricSpecialFilterState;
  onSelectUserMetricSpecialComparator?: (kind: ValueComparatorKind) => void;
  onCommitUserMetricSpecialState?: (state: UserMetricSpecialFilterState) => void;
  isLastInteractionTagFilter?: boolean;
  lastInteractionTagState?: LastInteractionTagFilterState;
  onCommitLastInteractionTagState?: (state: LastInteractionTagFilterState) => void;
  isActiveRow?: boolean;
  aliasKindName?: KindAliasFn;
  projectId?: string;
  filters?: GenericFilter[];
  /** Time range used by the special-time additional-filter value fetch, so it
   * matches the surrounding filter component's other fetches. */
  timeRange?: TimeRangeConfig;
  /** Shown only when there are 2+ filter rows: first row is IF, rest are AND. */
  conditionLabel?: 'IF' | 'AND' | null;
  /** Renders a connector line below the badge when this row is not the last. */
  showConditionConnector?: boolean;
  /** Portal target for nested menus (needed when embedded inside a Dialog). */
  popoverContainer?: Element | null;
  allowedOperators?: FilterOperator[];
  invalidFilterColumns?: string[];
};
export function FilterContentV2Row(t0) {
  const $ = _c(258);
  const {
    fieldKind,
    operator,
    fieldLabel,
    valueLabel,
    containsText,
    onContainsTextChange,
    onApplyContains,
    onSelectField,
    onSelectOperator,
    onRemove,
    removeDisabled,
    fieldKinds,
    favoriteTypes,
    onToggleFavorite,
    filterSearch,
    setFilterSearch,
    sortFavoritesFirst,
    selectedFieldType,
    availableFieldTypes,
    onSelectFieldType,
    fieldListPrepend,
    savedFilterGroups,
    onSelectSavedFilterGroup,
    onDeleteSavedFilterGroup,
    favoriteSavedFilterGroups,
    onToggleFavoriteSavedFilterGroup,
    searchFilterValue,
    setSearchFilterValue,
    sortBy,
    onChangeSortBy,
    onChangeSortByDirection,
    availableFilterValues,
    isLoadingValues,
    targetFilterValues,
    hasSelectedAll,
    showSelectAll,
    onCheckSelectAll,
    onSelectFilterValue,
    volumeKind,
    defaultOpenPopover: t1,
    onOpenField,
    onOpenOperator,
    onOpenValue,
    openPopoverRequest,
    fieldPillClassNames,
    operatorPillClassNames,
    valuePillClassNames,
    isSpecialTimeFilter: t2,
    specialTimeKind: t3,
    specialTimeState,
    onSelectSpecialTimeKind,
    onCommitSpecialTimeState,
    isInteractionSpecialFilter: t4,
    interactionSpecialComparatorKind: t5,
    interactionSpecialState,
    onSelectInteractionSpecialComparator,
    onCommitInteractionSpecialState,
    isUserMetricSpecialFilter: t6,
    userMetricSpecialComparatorKind: t7,
    userMetricSpecialState,
    onSelectUserMetricSpecialComparator,
    onCommitUserMetricSpecialState,
    isLastInteractionTagFilter: t8,
    lastInteractionTagState,
    onCommitLastInteractionTagState,
    isActiveRow: t9,
    aliasKindName,
    projectId,
    filters: t10,
    timeRange,
    conditionLabel: t11,
    showConditionConnector: t12,
    popoverContainer,
    allowedOperators,
    invalidFilterColumns
  } = t0;
  const defaultOpenPopover = t1 === undefined ? null : t1;
  const isSpecialTimeFilter = t2 === undefined ? false : t2;
  const specialTimeKind = t3 === undefined ? null : t3;
  const isInteractionSpecialFilter = t4 === undefined ? false : t4;
  const interactionSpecialComparatorKind = t5 === undefined ? null : t5;
  const isUserMetricSpecialFilter = t6 === undefined ? false : t6;
  const userMetricSpecialComparatorKind = t7 === undefined ? null : t7;
  const isLastInteractionTagFilter = t8 === undefined ? false : t8;
  const isActiveRow = t9 === undefined ? false : t9;
  let t13;
  if ($[0] !== t10) {
    t13 = t10 === undefined ? [] : t10;
    $[0] = t10;
    $[1] = t13;
  } else {
    t13 = $[1];
  }
  const filters = t13;
  const conditionLabel = t11 === undefined ? null : t11;
  const showConditionConnector = t12 === undefined ? false : t12;
  const isSpecialPillFilter = isSpecialTimeFilter || isInteractionSpecialFilter || isUserMetricSpecialFilter;
  const [openPopover, setOpenPopover] = useState(defaultOpenPopover);
  const [stagedSpecialTimeState, setStagedSpecialTimeState] = useState(specialTimeState);
  const [stagedInteractionSpecialState, setStagedInteractionSpecialState] = useState(interactionSpecialState);
  const [stagedUserMetricSpecialState, setStagedUserMetricSpecialState] = useState(userMetricSpecialState);
  const [stagedLastInteractionTagState, setStagedLastInteractionTagState] = useState(lastInteractionTagState);
  const containsInputRef = useRef(null);
  let t14;
  if ($[2] !== containsText) {
    t14 = containsText.trim();
    $[2] = containsText;
    $[3] = t14;
  } else {
    t14 = $[3];
  }
  const lastAppliedContainsTextRef = useRef(t14);
  const wasActiveRowRef = useRef(isActiveRow);
  let t15;
  let t16;
  if ($[4] !== specialTimeState) {
    t15 = () => {
      if (specialTimeState) {
        setStagedSpecialTimeState(specialTimeState);
      }
    };
    t16 = [specialTimeState];
    $[4] = specialTimeState;
    $[5] = t15;
    $[6] = t16;
  } else {
    t15 = $[5];
    t16 = $[6];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[7] !== interactionSpecialState) {
    t17 = () => {
      if (interactionSpecialState) {
        setStagedInteractionSpecialState(interactionSpecialState);
      }
    };
    t18 = [interactionSpecialState];
    $[7] = interactionSpecialState;
    $[8] = t17;
    $[9] = t18;
  } else {
    t17 = $[8];
    t18 = $[9];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  if ($[10] !== userMetricSpecialState) {
    t19 = () => {
      if (userMetricSpecialState) {
        setStagedUserMetricSpecialState(userMetricSpecialState);
      }
    };
    t20 = [userMetricSpecialState];
    $[10] = userMetricSpecialState;
    $[11] = t19;
    $[12] = t20;
  } else {
    t19 = $[11];
    t20 = $[12];
  }
  useEffect(t19, t20);
  let t21;
  let t22;
  if ($[13] !== lastInteractionTagState) {
    t21 = () => {
      if (lastInteractionTagState) {
        setStagedLastInteractionTagState(lastInteractionTagState);
      }
    };
    t22 = [lastInteractionTagState];
    $[13] = lastInteractionTagState;
    $[14] = t21;
    $[15] = t22;
  } else {
    t21 = $[14];
    t22 = $[15];
  }
  useEffect(t21, t22);
  let t23;
  let t24;
  if ($[16] !== containsText || $[17] !== isActiveRow) {
    t23 = () => {
      if (isActiveRow && !wasActiveRowRef.current) {
        lastAppliedContainsTextRef.current = containsText.trim();
      }
      wasActiveRowRef.current = isActiveRow;
    };
    t24 = [containsText, isActiveRow];
    $[16] = containsText;
    $[17] = isActiveRow;
    $[18] = t23;
    $[19] = t24;
  } else {
    t23 = $[18];
    t24 = $[19];
  }
  useEffect(t23, t24);
  let t25;
  if ($[20] !== aliasKindName || $[21] !== fieldKind || $[22] !== fieldLabel) {
    t25 = fieldKind ? fieldLabel ?? buildFilterKindName(fieldKind, aliasKindName) : undefined;
    $[20] = aliasKindName;
    $[21] = fieldKind;
    $[22] = fieldLabel;
    $[23] = t25;
  } else {
    t25 = $[23];
  }
  const fieldDisplay = t25;
  let t26;
  if ($[24] !== interactionSpecialComparatorKind || $[25] !== isInteractionSpecialFilter || $[26] !== isSpecialTimeFilter || $[27] !== isUserMetricSpecialFilter || $[28] !== operator || $[29] !== specialTimeKind || $[30] !== userMetricSpecialComparatorKind) {
    t26 = isSpecialTimeFilter ? specialTimeKind ? getSpecialTimeKindLabel(specialTimeKind) : undefined : isInteractionSpecialFilter || isUserMetricSpecialFilter ? (isInteractionSpecialFilter ? interactionSpecialComparatorKind : userMetricSpecialComparatorKind) ? getValueComparatorLabel((isInteractionSpecialFilter ? interactionSpecialComparatorKind : userMetricSpecialComparatorKind) as ValueComparatorKind) : undefined : operator ? getOperatorLabel(operator) : undefined;
    $[24] = interactionSpecialComparatorKind;
    $[25] = isInteractionSpecialFilter;
    $[26] = isSpecialTimeFilter;
    $[27] = isUserMetricSpecialFilter;
    $[28] = operator;
    $[29] = specialTimeKind;
    $[30] = userMetricSpecialComparatorKind;
    $[31] = t26;
  } else {
    t26 = $[31];
  }
  const operatorDisplay = t26;
  const isStandardOperatorDropdown = !isSpecialTimeFilter && !isInteractionSpecialFilter && !isUserMetricSpecialFilter && !isLastInteractionTagFilter;
  let t27;
  if ($[32] !== allowedOperators || $[33] !== isStandardOperatorDropdown) {
    t27 = isStandardOperatorDropdown && hasSingleOperatorChoice(allowedOperators);
    $[32] = allowedOperators;
    $[33] = isStandardOperatorDropdown;
    $[34] = t27;
  } else {
    t27 = $[34];
  }
  const operatorSelectionLocked = t27;
  const isOperatorDisabled = !fieldKind || operatorSelectionLocked;
  let t28;
  let t29;
  if ($[35] !== openPopoverRequest || $[36] !== operatorSelectionLocked) {
    t28 = () => {
      if (openPopoverRequest) {
        const target = openPopoverRequest.target === "operator" && operatorSelectionLocked ? "value" : openPopoverRequest.target;
        setOpenPopover(target);
      }
    };
    t29 = [openPopoverRequest, operatorSelectionLocked];
    $[35] = openPopoverRequest;
    $[36] = operatorSelectionLocked;
    $[37] = t28;
    $[38] = t29;
  } else {
    t28 = $[37];
    t29 = $[38];
  }
  useEffect(t28, t29);
  const isValueDisabled = isSpecialTimeFilter ? !fieldKind || !specialTimeKind : isInteractionSpecialFilter ? !fieldKind || !interactionSpecialComparatorKind : isUserMetricSpecialFilter ? !fieldKind || !userMetricSpecialComparatorKind : !fieldKind || !operator;
  let t30;
  if ($[39] !== containsText || $[40] !== fieldKind || $[41] !== isValueDisabled || $[42] !== onApplyContains || $[43] !== operator) {
    t30 = textOverride => {
      if (operator !== "contains" || isValueDisabled || !fieldKind) {
        return;
      }
      const text = (textOverride ?? containsText).trim();
      if (text === lastAppliedContainsTextRef.current) {
        return;
      }
      lastAppliedContainsTextRef.current = text;
      onApplyContains(text, fieldKind);
    };
    $[39] = containsText;
    $[40] = fieldKind;
    $[41] = isValueDisabled;
    $[42] = onApplyContains;
    $[43] = operator;
    $[44] = t30;
  } else {
    t30 = $[44];
  }
  const flushContainsIfChanged = t30;
  let t31;
  if ($[45] !== flushContainsIfChanged) {
    t31 = () => {
      flushContainsIfChanged();
    };
    $[45] = flushContainsIfChanged;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  const applyContainsNow = t31;
  let t32;
  if ($[47] !== containsText || $[48] !== fieldKind || $[49] !== isLastInteractionTagFilter || $[50] !== isSpecialPillFilter || $[51] !== operator || $[52] !== targetFilterValues || $[53] !== valueLabel) {
    t32 = isSpecialPillFilter || isLastInteractionTagFilter ? valueLabel : operator === "contains" ? containsText.trim() || undefined : valueLabel || (fieldKind && targetFilterValues.length > 0 ? formatValuesForPillDisplay(fieldKind, targetFilterValues) : undefined);
    $[47] = containsText;
    $[48] = fieldKind;
    $[49] = isLastInteractionTagFilter;
    $[50] = isSpecialPillFilter;
    $[51] = operator;
    $[52] = targetFilterValues;
    $[53] = valueLabel;
    $[54] = t32;
  } else {
    t32 = $[54];
  }
  const valueDisplay = t32;
  const isValuePlaceholder = !valueDisplay;
  let t33;
  let t34;
  if ($[55] !== containsText || $[56] !== isActiveRow || $[57] !== isValueDisabled || $[58] !== onApplyContains || $[59] !== operator) {
    t33 = () => {
      const text_0 = containsText.trim();
      if (!isActiveRow || operator !== "contains" || isValueDisabled || text_0 === lastAppliedContainsTextRef.current) {
        return;
      }
      const timeout = window.setTimeout(() => {
        if (text_0 === lastAppliedContainsTextRef.current) {
          return;
        }
        lastAppliedContainsTextRef.current = text_0;
        onApplyContains();
      }, 1000);
      return () => window.clearTimeout(timeout);
    };
    t34 = [containsText, isActiveRow, isValueDisabled, onApplyContains, operator];
    $[55] = containsText;
    $[56] = isActiveRow;
    $[57] = isValueDisabled;
    $[58] = onApplyContains;
    $[59] = operator;
    $[60] = t33;
    $[61] = t34;
  } else {
    t33 = $[60];
    t34 = $[61];
  }
  useEffect(t33, t34);
  let t35;
  if ($[62] !== interactionSpecialState || $[63] !== isInteractionSpecialFilter || $[64] !== isLastInteractionTagFilter || $[65] !== isSpecialTimeFilter || $[66] !== isUserMetricSpecialFilter || $[67] !== isValueDisabled || $[68] !== lastInteractionTagState || $[69] !== onOpenValue || $[70] !== specialTimeState || $[71] !== userMetricSpecialState) {
    t35 = () => {
      if (isValueDisabled) {
        return;
      }
      if (isSpecialTimeFilter && specialTimeState) {
        setStagedSpecialTimeState(specialTimeState);
      }
      if (isInteractionSpecialFilter && interactionSpecialState) {
        setStagedInteractionSpecialState(interactionSpecialState);
      }
      if (isUserMetricSpecialFilter && userMetricSpecialState) {
        setStagedUserMetricSpecialState(userMetricSpecialState);
      }
      if (isLastInteractionTagFilter && lastInteractionTagState) {
        setStagedLastInteractionTagState(lastInteractionTagState);
      }
      onOpenValue?.();
      setOpenPopover("value");
    };
    $[62] = interactionSpecialState;
    $[63] = isInteractionSpecialFilter;
    $[64] = isLastInteractionTagFilter;
    $[65] = isSpecialTimeFilter;
    $[66] = isUserMetricSpecialFilter;
    $[67] = isValueDisabled;
    $[68] = lastInteractionTagState;
    $[69] = onOpenValue;
    $[70] = specialTimeState;
    $[71] = userMetricSpecialState;
    $[72] = t35;
  } else {
    t35 = $[72];
  }
  const openValuePopover = t35;
  let t36;
  if ($[73] !== onCommitSpecialTimeState || $[74] !== stagedSpecialTimeState) {
    t36 = t37 => {
      const state = t37 === undefined ? stagedSpecialTimeState : t37;
      if (!state) {
        return;
      }
      onCommitSpecialTimeState?.(state);
    };
    $[73] = onCommitSpecialTimeState;
    $[74] = stagedSpecialTimeState;
    $[75] = t36;
  } else {
    t36 = $[75];
  }
  const commitSpecialTimeState = t36;
  let t37;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = nextState => {
      setStagedSpecialTimeState(nextState);
    };
    $[76] = t37;
  } else {
    t37 = $[76];
  }
  const handleSpecialTimeStateChange = t37;
  let t38;
  if ($[77] !== onCommitInteractionSpecialState || $[78] !== stagedInteractionSpecialState) {
    t38 = t39 => {
      const state_0 = t39 === undefined ? stagedInteractionSpecialState : t39;
      if (!state_0) {
        return;
      }
      onCommitInteractionSpecialState?.(state_0);
    };
    $[77] = onCommitInteractionSpecialState;
    $[78] = stagedInteractionSpecialState;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  const commitInteractionSpecialState = t38;
  let t39;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = nextState_0 => {
      setStagedInteractionSpecialState(nextState_0);
    };
    $[80] = t39;
  } else {
    t39 = $[80];
  }
  const handleInteractionSpecialStateChange = t39;
  let t40;
  if ($[81] !== onCommitUserMetricSpecialState || $[82] !== stagedUserMetricSpecialState) {
    t40 = t41 => {
      const state_1 = t41 === undefined ? stagedUserMetricSpecialState : t41;
      if (!state_1) {
        return;
      }
      onCommitUserMetricSpecialState?.(state_1);
    };
    $[81] = onCommitUserMetricSpecialState;
    $[82] = stagedUserMetricSpecialState;
    $[83] = t40;
  } else {
    t40 = $[83];
  }
  const commitUserMetricSpecialState = t40;
  let t41;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = nextState_1 => {
      setStagedUserMetricSpecialState(nextState_1);
    };
    $[84] = t41;
  } else {
    t41 = $[84];
  }
  const handleUserMetricSpecialStateChange = t41;
  let t42;
  if ($[85] !== interactionSpecialState || $[86] !== isInteractionSpecialFilter || $[87] !== isLastInteractionTagFilter || $[88] !== isSpecialTimeFilter || $[89] !== isUserMetricSpecialFilter || $[90] !== lastInteractionTagState || $[91] !== specialTimeState || $[92] !== userMetricSpecialState) {
    t42 = () => {
      if (isSpecialTimeFilter) {
        setStagedSpecialTimeState(specialTimeState);
      }
      if (isInteractionSpecialFilter) {
        setStagedInteractionSpecialState(interactionSpecialState);
      }
      if (isUserMetricSpecialFilter) {
        setStagedUserMetricSpecialState(userMetricSpecialState);
      }
      if (isLastInteractionTagFilter) {
        setStagedLastInteractionTagState(lastInteractionTagState);
      }
      setOpenPopover(null);
    };
    $[85] = interactionSpecialState;
    $[86] = isInteractionSpecialFilter;
    $[87] = isLastInteractionTagFilter;
    $[88] = isSpecialTimeFilter;
    $[89] = isUserMetricSpecialFilter;
    $[90] = lastInteractionTagState;
    $[91] = specialTimeState;
    $[92] = userMetricSpecialState;
    $[93] = t42;
  } else {
    t42 = $[93];
  }
  const closeSpecialPopoverWithoutApply = t42;
  const isValueTriggerClick = _temp;
  let t43;
  if ($[94] !== fieldPillClassNames) {
    t43 = mergePillClassNames({
      label: PILL_LABEL_CLASS_NAMES,
      chevron: PILL_CHEVRON_CLASS_NAMES
    }, fieldPillClassNames);
    $[94] = fieldPillClassNames;
    $[95] = t43;
  } else {
    t43 = $[95];
  }
  const fieldPillClassNamesMerged = t43;
  let t44;
  if ($[96] !== operatorPillClassNames) {
    t44 = mergePillClassNames({
      label: PILL_LABEL_CLASS_NAMES,
      chevron: PILL_CHEVRON_CLASS_NAMES
    }, operatorPillClassNames);
    $[96] = operatorPillClassNames;
    $[97] = t44;
  } else {
    t44 = $[97];
  }
  const operatorPillClassNamesMerged = t44;
  let t45;
  if ($[98] !== valuePillClassNames) {
    t45 = mergePillClassNames({
      label: PILL_LABEL_CLASS_NAMES,
      chevron: PILL_CHEVRON_CLASS_NAMES
    }, valuePillClassNames);
    $[98] = valuePillClassNames;
    $[99] = t45;
  } else {
    t45 = $[99];
  }
  const valuePillClassNamesMerged = t45;
  let t46;
  if ($[100] !== fieldKind || $[101] !== invalidFilterColumns) {
    t46 = fieldKind ? invalidFilterColumns?.includes(fieldKind) ?? false : false;
    $[100] = fieldKind;
    $[101] = invalidFilterColumns;
    $[102] = t46;
  } else {
    t46 = $[102];
  }
  const isValueInvalid = t46;
  const shouldHighlightValuePill = !isValueDisabled && !isValueInvalid && (openPopover === "value" || isValuePlaceholder);
  let t47;
  if ($[103] !== conditionLabel || $[104] !== showConditionConnector) {
    t47 = conditionLabel ? <div className="relative flex w-8 shrink-0 flex-col items-center self-stretch"><span className="flex h-8 w-full items-center justify-center rounded-md border border-zinc-800 bg-zinc-900 text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">{conditionLabel}</span>{showConditionConnector && <span className="absolute top-8 -bottom-3 left-1/2 h-4 w-px -translate-x-1/2 bg-primary/60" aria-hidden={true} />}</div> : null;
    $[103] = conditionLabel;
    $[104] = showConditionConnector;
    $[105] = t47;
  } else {
    t47 = $[105];
  }
  const t48 = openPopover === "field";
  let t49;
  if ($[106] !== onOpenField) {
    t49 = open => {
      if (open) {
        onOpenField?.();
      }
      setOpenPopover(open ? "field" : null);
    };
    $[106] = onOpenField;
    $[107] = t49;
  } else {
    t49 = $[107];
  }
  const t50 = fieldDisplay ?? "Select";
  const t51 = !fieldKind;
  let t52;
  if ($[108] !== fieldPillClassNamesMerged || $[109] !== t50 || $[110] !== t51) {
    t52 = <PopoverTrigger asChild={true}><div><FilterContentV2Pill label={t50} placeholder="Select" isPlaceholder={t51} className="h-8 w-32" classNames={fieldPillClassNamesMerged} /></div></PopoverTrigger>;
    $[108] = fieldPillClassNamesMerged;
    $[109] = t50;
    $[110] = t51;
    $[111] = t52;
  } else {
    t52 = $[111];
  }
  let t53;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = cn(FILTER_CONTENT_V2_POPOVER_CONTENT_CLASS, "w-auto border-0 bg-transparent p-0 shadow-none");
    $[112] = t53;
  } else {
    t53 = $[112];
  }
  let t54;
  if ($[113] !== onSelectField || $[114] !== operatorSelectionLocked) {
    t54 = kind => {
      onSelectField(kind);
      setOpenPopover(operatorSelectionLocked ? "value" : "operator");
    };
    $[113] = onSelectField;
    $[114] = operatorSelectionLocked;
    $[115] = t54;
  } else {
    t54 = $[115];
  }
  let t55;
  if ($[116] !== aliasKindName || $[117] !== availableFieldTypes || $[118] !== favoriteSavedFilterGroups || $[119] !== favoriteTypes || $[120] !== fieldKind || $[121] !== fieldKinds || $[122] !== fieldListPrepend || $[123] !== filterSearch || $[124] !== onDeleteSavedFilterGroup || $[125] !== onSelectFieldType || $[126] !== onSelectSavedFilterGroup || $[127] !== onToggleFavorite || $[128] !== onToggleFavoriteSavedFilterGroup || $[129] !== savedFilterGroups || $[130] !== selectedFieldType || $[131] !== setFilterSearch || $[132] !== sortFavoritesFirst || $[133] !== t54) {
    t55 = <FilterContentV2FieldDropdown kinds={fieldKinds} aliasKindName={aliasKindName} favoriteTypes={favoriteTypes} selectedKind={fieldKind} onSelectKind={t54} onToggleFavorite={onToggleFavorite} filterSearch={filterSearch} setFilterSearch={setFilterSearch} listPrepend={fieldListPrepend} sortFavoritesFirst={sortFavoritesFirst} selectedType={selectedFieldType} availableTypes={availableFieldTypes} onSelectType={onSelectFieldType} savedFilterGroups={savedFilterGroups} onSelectSavedFilterGroup={onSelectSavedFilterGroup} onDeleteSavedFilterGroup={onDeleteSavedFilterGroup} favoriteSavedFilterGroups={favoriteSavedFilterGroups} onToggleFavoriteSavedFilterGroup={onToggleFavoriteSavedFilterGroup} />;
    $[116] = aliasKindName;
    $[117] = availableFieldTypes;
    $[118] = favoriteSavedFilterGroups;
    $[119] = favoriteTypes;
    $[120] = fieldKind;
    $[121] = fieldKinds;
    $[122] = fieldListPrepend;
    $[123] = filterSearch;
    $[124] = onDeleteSavedFilterGroup;
    $[125] = onSelectFieldType;
    $[126] = onSelectSavedFilterGroup;
    $[127] = onToggleFavorite;
    $[128] = onToggleFavoriteSavedFilterGroup;
    $[129] = savedFilterGroups;
    $[130] = selectedFieldType;
    $[131] = setFilterSearch;
    $[132] = sortFavoritesFirst;
    $[133] = t54;
    $[134] = t55;
  } else {
    t55 = $[134];
  }
  let t56;
  if ($[135] !== popoverContainer || $[136] !== t55) {
    t56 = <PopoverContent className={t53} align="start" side="bottom" container={popoverContainer} onWheel={stopPopoverWheelPropagation}>{t55}</PopoverContent>;
    $[135] = popoverContainer;
    $[136] = t55;
    $[137] = t56;
  } else {
    t56 = $[137];
  }
  let t57;
  if ($[138] !== t48 || $[139] !== t49 || $[140] !== t52 || $[141] !== t56) {
    t57 = <Popover modal={false} open={t48} onOpenChange={t49}>{t52}{t56}</Popover>;
    $[138] = t48;
    $[139] = t49;
    $[140] = t52;
    $[141] = t56;
    $[142] = t57;
  } else {
    t57 = $[142];
  }
  const t58 = openPopover === "operator";
  let t59;
  if ($[143] !== isOperatorDisabled || $[144] !== onOpenOperator || $[145] !== operatorSelectionLocked) {
    t59 = open_0 => {
      if (open_0 && (isOperatorDisabled || operatorSelectionLocked)) {
        return;
      }
      if (open_0) {
        onOpenOperator?.();
        setOpenPopover("operator");
        return;
      }
      setOpenPopover(_temp2);
    };
    $[143] = isOperatorDisabled;
    $[144] = onOpenOperator;
    $[145] = operatorSelectionLocked;
    $[146] = t59;
  } else {
    t59 = $[146];
  }
  const t60 = isOperatorDisabled ? "cursor-not-allowed" : undefined;
  let t61;
  if ($[147] !== isInteractionSpecialFilter || $[148] !== isSpecialTimeFilter || $[149] !== isUserMetricSpecialFilter || $[150] !== operatorDisplay) {
    t61 = operatorDisplay ?? (isSpecialTimeFilter ? getSpecialTimeKindLabel(InTheLastValueConfigKind.in_the_last) : isInteractionSpecialFilter || isUserMetricSpecialFilter ? getValueComparatorLabel(AboveValueConfigKind.above) : "Equals");
    $[147] = isInteractionSpecialFilter;
    $[148] = isSpecialTimeFilter;
    $[149] = isUserMetricSpecialFilter;
    $[150] = operatorDisplay;
    $[151] = t61;
  } else {
    t61 = $[151];
  }
  let t62;
  if ($[152] !== isInteractionSpecialFilter || $[153] !== isSpecialTimeFilter || $[154] !== isUserMetricSpecialFilter) {
    t62 = isSpecialTimeFilter ? getSpecialTimeKindLabel(InTheLastValueConfigKind.in_the_last) : isInteractionSpecialFilter || isUserMetricSpecialFilter ? getValueComparatorLabel(AboveValueConfigKind.above) : "Equals";
    $[152] = isInteractionSpecialFilter;
    $[153] = isSpecialTimeFilter;
    $[154] = isUserMetricSpecialFilter;
    $[155] = t62;
  } else {
    t62 = $[155];
  }
  const t63 = isSpecialTimeFilter ? !specialTimeKind : isInteractionSpecialFilter ? !interactionSpecialComparatorKind : isUserMetricSpecialFilter ? !userMetricSpecialComparatorKind : !operator;
  let t64;
  if ($[156] !== isOperatorDisabled || $[157] !== operatorPillClassNamesMerged || $[158] !== operatorSelectionLocked || $[159] !== t61 || $[160] !== t62 || $[161] !== t63) {
    t64 = <FilterContentV2Pill label={t61} placeholder={t62} isPlaceholder={t63} disabled={isOperatorDisabled} className="h-8 w-24" hideChevron={operatorSelectionLocked} classNames={operatorPillClassNamesMerged} />;
    $[156] = isOperatorDisabled;
    $[157] = operatorPillClassNamesMerged;
    $[158] = operatorSelectionLocked;
    $[159] = t61;
    $[160] = t62;
    $[161] = t63;
    $[162] = t64;
  } else {
    t64 = $[162];
  }
  let t65;
  if ($[163] !== t60 || $[164] !== t64) {
    t65 = <div className={t60}>{t64}</div>;
    $[163] = t60;
    $[164] = t64;
    $[165] = t65;
  } else {
    t65 = $[165];
  }
  let t66;
  if ($[166] !== isOperatorDisabled || $[167] !== t65) {
    t66 = <PopoverTrigger asChild={true} disabled={isOperatorDisabled}>{t65}</PopoverTrigger>;
    $[166] = isOperatorDisabled;
    $[167] = t65;
    $[168] = t66;
  } else {
    t66 = $[168];
  }
  let t67;
  if ($[169] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = cn(FILTER_CONTENT_V2_POPOVER_CONTENT_CLASS, "w-52 rounded-md border border-zinc-800 bg-transparent p-0 shadow-none");
    $[169] = t67;
  } else {
    t67 = $[169];
  }
  let t68;
  let t69;
  if ($[170] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = event => {
      if (isValueTriggerClick(event.target)) {
        event.preventDefault();
      }
    };
    t69 = event_0 => {
      if (isValueTriggerClick(event_0.target)) {
        event_0.preventDefault();
      }
    };
    $[170] = t68;
    $[171] = t69;
  } else {
    t68 = $[170];
    t69 = $[171];
  }
  let t70;
  if ($[172] !== allowedOperators || $[173] !== interactionSpecialComparatorKind || $[174] !== isActiveRow || $[175] !== isInteractionSpecialFilter || $[176] !== isSpecialTimeFilter || $[177] !== isUserMetricSpecialFilter || $[178] !== onSelectInteractionSpecialComparator || $[179] !== onSelectOperator || $[180] !== onSelectSpecialTimeKind || $[181] !== onSelectUserMetricSpecialComparator || $[182] !== operator || $[183] !== specialTimeKind || $[184] !== userMetricSpecialComparatorKind) {
    t70 = isSpecialTimeFilter ? <FilterContentV2SpecialTimeKindDropdown selectedKind={specialTimeKind} onSelectKind={kind_0 => {
      onSelectSpecialTimeKind?.(kind_0);
      setOpenPopover("value");
    }} /> : isInteractionSpecialFilter || isUserMetricSpecialFilter ? <FilterContentV2InteractionSpecialKindDropdown selectedKind={(isInteractionSpecialFilter ? interactionSpecialComparatorKind : userMetricSpecialComparatorKind) ?? null} onSelectKind={kind_1 => {
      if (isInteractionSpecialFilter) {
        onSelectInteractionSpecialComparator?.(kind_1);
      } else {
        onSelectUserMetricSpecialComparator?.(kind_1);
      }
      setOpenPopover("value");
    }} /> : <FilterContentV2OperatorDropdown selectedOperator={operator} allowedOperators={allowedOperators} onSelectOperator={op => {
      onSelectOperator(op);
      setOpenPopover(op === "contains" ? null : "value");
      if (op === "contains" && isActiveRow) {
        focusContainsInput(containsInputRef);
      }
    }} />;
    $[172] = allowedOperators;
    $[173] = interactionSpecialComparatorKind;
    $[174] = isActiveRow;
    $[175] = isInteractionSpecialFilter;
    $[176] = isSpecialTimeFilter;
    $[177] = isUserMetricSpecialFilter;
    $[178] = onSelectInteractionSpecialComparator;
    $[179] = onSelectOperator;
    $[180] = onSelectSpecialTimeKind;
    $[181] = onSelectUserMetricSpecialComparator;
    $[182] = operator;
    $[183] = specialTimeKind;
    $[184] = userMetricSpecialComparatorKind;
    $[185] = t70;
  } else {
    t70 = $[185];
  }
  let t71;
  if ($[186] !== popoverContainer || $[187] !== t70) {
    t71 = <PopoverContent className={t67} align="start" container={popoverContainer} onWheel={stopPopoverWheelPropagation} onInteractOutside={t68} onPointerDownOutside={t69}>{t70}</PopoverContent>;
    $[186] = popoverContainer;
    $[187] = t70;
    $[188] = t71;
  } else {
    t71 = $[188];
  }
  let t72;
  if ($[189] !== t58 || $[190] !== t59 || $[191] !== t66 || $[192] !== t71) {
    t72 = <Popover modal={false} open={t58} onOpenChange={t59}>{t66}{t71}</Popover>;
    $[189] = t58;
    $[190] = t59;
    $[191] = t66;
    $[192] = t71;
    $[193] = t72;
  } else {
    t72 = $[193];
  }
  let t73;
  if ($[194] !== applyContainsNow || $[195] !== availableFilterValues || $[196] !== closeSpecialPopoverWithoutApply || $[197] !== commitInteractionSpecialState || $[198] !== commitSpecialTimeState || $[199] !== commitUserMetricSpecialState || $[200] !== containsText || $[201] !== fieldKind || $[202] !== filters || $[203] !== flushContainsIfChanged || $[204] !== hasSelectedAll || $[205] !== interactionSpecialComparatorKind || $[206] !== isActiveRow || $[207] !== isInteractionSpecialFilter || $[208] !== isLastInteractionTagFilter || $[209] !== isLoadingValues || $[210] !== isSpecialPillFilter || $[211] !== isSpecialTimeFilter || $[212] !== isUserMetricSpecialFilter || $[213] !== isValueDisabled || $[214] !== isValueInvalid || $[215] !== isValuePlaceholder || $[216] !== onApplyContains || $[217] !== onChangeSortBy || $[218] !== onChangeSortByDirection || $[219] !== onCheckSelectAll || $[220] !== onCommitLastInteractionTagState || $[221] !== onContainsTextChange || $[222] !== onOpenValue || $[223] !== onSelectFilterValue || $[224] !== openPopover || $[225] !== openValuePopover || $[226] !== operator || $[227] !== popoverContainer || $[228] !== projectId || $[229] !== searchFilterValue || $[230] !== setSearchFilterValue || $[231] !== shouldHighlightValuePill || $[232] !== showSelectAll || $[233] !== sortBy || $[234] !== specialTimeKind || $[235] !== stagedInteractionSpecialState || $[236] !== stagedLastInteractionTagState || $[237] !== stagedSpecialTimeState || $[238] !== stagedUserMetricSpecialState || $[239] !== targetFilterValues || $[240] !== timeRange || $[241] !== userMetricSpecialComparatorKind || $[242] !== valueDisplay || $[243] !== valuePillClassNamesMerged || $[244] !== volumeKind) {
    t73 = isSpecialPillFilter || isLastInteractionTagFilter || operator !== "contains" ? <div className="min-w-0 flex-1"><Popover modal={false} open={openPopover === "value"} onOpenChange={open_1 => {
        if (open_1) {
          return;
        }
        closeSpecialPopoverWithoutApply();
      }}><PopoverTrigger asChild={true} disabled={isValueDisabled}><div className={cn("min-w-0 flex-1", isValueDisabled ? "cursor-not-allowed" : "cursor-pointer")}><FilterContentV2Pill data-filter-value-trigger={true} className="h-8 w-full max-w-none min-w-30" label={valueDisplay ?? "Select value"} tooltip={isSpecialPillFilter || isLastInteractionTagFilter ? valueDisplay : undefined} placeholder="Select value" isPlaceholder={isValuePlaceholder} disabled={isValueDisabled} highlight={shouldHighlightValuePill} invalid={isValueInvalid} hideChevron={true} classNames={valuePillClassNamesMerged} onClick={event_1 => {
              if (isValueDisabled) {
                return;
              }
              event_1?.preventDefault();
              if (openPopover === "value") {
                closeSpecialPopoverWithoutApply();
                return;
              }
              openValuePopover();
            }} /></div></PopoverTrigger><PopoverContent className={cn(FILTER_CONTENT_V2_POPOVER_CONTENT_CLASS, "w-auto border-0 bg-transparent p-0 shadow-none")} align="start" container={popoverContainer} onWheel={stopPopoverWheelPropagation}>{isSpecialTimeFilter && specialTimeKind && stagedSpecialTimeState ? <FilterContentV2SpecialTimeValuePopover key={`${fieldKind}-${specialTimeKind}-${openPopover === "value" ? "open" : "closed"}`} kind={specialTimeKind} timeRange={timeRange} initialState={stagedSpecialTimeState} onChange={handleSpecialTimeStateChange} onApply={state_2 => {
            setStagedSpecialTimeState(state_2);
            commitSpecialTimeState(state_2);
            setOpenPopover(null);
          }} /> : isInteractionSpecialFilter && interactionSpecialComparatorKind && stagedInteractionSpecialState && fieldKind ? <FilterContentV2InteractionSpecialValuePopover key={`${fieldKind}-${interactionSpecialComparatorKind}-${openPopover === "value" ? "open" : "closed"}`} column={fieldKind} initialState={stagedInteractionSpecialState} onChange={handleInteractionSpecialStateChange} onApply={state_3 => {
            setStagedInteractionSpecialState(state_3);
            commitInteractionSpecialState(state_3);
            setOpenPopover(null);
          }} /> : isUserMetricSpecialFilter && userMetricSpecialComparatorKind && stagedUserMetricSpecialState ? <FilterContentV2UserMetricSpecialValuePopover key={`${fieldKind}-${userMetricSpecialComparatorKind}-${openPopover === "value" ? "open" : "closed"}`} initialState={stagedUserMetricSpecialState} onChange={handleUserMetricSpecialStateChange} onApply={state_4 => {
            setStagedUserMetricSpecialState(state_4);
            commitUserMetricSpecialState(state_4);
            setOpenPopover(null);
          }} /> : isLastInteractionTagFilter && operator && stagedLastInteractionTagState && projectId ? <FilterContentV2LastInteractionTagValuePopover key={`${fieldKind}-${operator}-${openPopover === "value" ? "open" : "closed"}`} operator={operator} initialState={stagedLastInteractionTagState} projectId={projectId} timeRange={timeRange} volumeKind={volumeKind} filters={filters} onChange={setStagedLastInteractionTagState} onApply={state_5 => {
            setStagedLastInteractionTagState(state_5);
            onCommitLastInteractionTagState?.(state_5);
            setOpenPopover(null);
          }} /> : !isSpecialPillFilter && !isLastInteractionTagFilter && fieldKind && operator ? <FilterContentV2ValueDropdown filterKind={fieldKind} operator={operator} containsText={containsText} onContainsTextChange={onContainsTextChange} onApplyContains={() => {
            onApplyContains();
            setOpenPopover(null);
          }} searchFilterValue={searchFilterValue} setSearchFilterValue={setSearchFilterValue} sortBy={sortBy} onChangeSortBy={onChangeSortBy} onChangeSortByDirection={onChangeSortByDirection} availableFilterValues={availableFilterValues} isLoading={isLoadingValues} targetFilterValues={targetFilterValues} hasSelectedAll={hasSelectedAll} showSelectAll={showSelectAll} onCheckSelectAll={onCheckSelectAll} onSelectFilterValue={onSelectFilterValue} volumeKind={volumeKind} /> : null}</PopoverContent></Popover></div> : <div className={cn("flex h-8 min-w-0 flex-1 items-center rounded-base border border-zinc-800 bg-[#09090B] px-2 opacity-80", isValueDisabled && "cursor-not-allowed", !isValueDisabled && (isValuePlaceholder || openPopover === "value") && "border-white!")}><Input ref={containsInputRef} placeholder="Type value" className="h-5 min-w-0 flex-1 border-0 bg-transparent px-0.5 text-[12px] font-semibold text-white shadow-none focus-visible:ring-0 disabled:pointer-events-none" value={containsText} disabled={isValueDisabled} onMouseDown={() => {
        if (!isActiveRow) {
          onOpenValue?.();
        }
      }} onChange={e => {
        if (!isActiveRow) {
          return;
        }
        onContainsTextChange(e.currentTarget.value);
      }} onFocus={() => {
        onOpenValue?.();
        setOpenPopover("value");
      }} onBlur={e_0 => {
        flushContainsIfChanged(e_0.currentTarget.value);
        setOpenPopover(_temp3);
      }} onKeyDown={e_1 => {
        if (e_1.key !== "Enter") {
          return;
        }
        e_1.preventDefault();
        applyContainsNow();
      }} /></div>;
    $[194] = applyContainsNow;
    $[195] = availableFilterValues;
    $[196] = closeSpecialPopoverWithoutApply;
    $[197] = commitInteractionSpecialState;
    $[198] = commitSpecialTimeState;
    $[199] = commitUserMetricSpecialState;
    $[200] = containsText;
    $[201] = fieldKind;
    $[202] = filters;
    $[203] = flushContainsIfChanged;
    $[204] = hasSelectedAll;
    $[205] = interactionSpecialComparatorKind;
    $[206] = isActiveRow;
    $[207] = isInteractionSpecialFilter;
    $[208] = isLastInteractionTagFilter;
    $[209] = isLoadingValues;
    $[210] = isSpecialPillFilter;
    $[211] = isSpecialTimeFilter;
    $[212] = isUserMetricSpecialFilter;
    $[213] = isValueDisabled;
    $[214] = isValueInvalid;
    $[215] = isValuePlaceholder;
    $[216] = onApplyContains;
    $[217] = onChangeSortBy;
    $[218] = onChangeSortByDirection;
    $[219] = onCheckSelectAll;
    $[220] = onCommitLastInteractionTagState;
    $[221] = onContainsTextChange;
    $[222] = onOpenValue;
    $[223] = onSelectFilterValue;
    $[224] = openPopover;
    $[225] = openValuePopover;
    $[226] = operator;
    $[227] = popoverContainer;
    $[228] = projectId;
    $[229] = searchFilterValue;
    $[230] = setSearchFilterValue;
    $[231] = shouldHighlightValuePill;
    $[232] = showSelectAll;
    $[233] = sortBy;
    $[234] = specialTimeKind;
    $[235] = stagedInteractionSpecialState;
    $[236] = stagedLastInteractionTagState;
    $[237] = stagedSpecialTimeState;
    $[238] = stagedUserMetricSpecialState;
    $[239] = targetFilterValues;
    $[240] = timeRange;
    $[241] = userMetricSpecialComparatorKind;
    $[242] = valueDisplay;
    $[243] = valuePillClassNamesMerged;
    $[244] = volumeKind;
    $[245] = t73;
  } else {
    t73 = $[245];
  }
  let t74;
  if ($[246] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <X className="h-3.5 w-3.5" />;
    $[246] = t74;
  } else {
    t74 = $[246];
  }
  let t75;
  if ($[247] !== onRemove || $[248] !== removeDisabled) {
    t75 = <Button type="button" variant="outline" size="icon" disabled={removeDisabled} className="h-8 w-8 shrink-0 rounded-base border-zinc-800 bg-[#09090B] text-white opacity-80 disabled:pointer-events-auto disabled:cursor-not-allowed" onClick={onRemove} aria-label="Remove filter row">{t74}</Button>;
    $[247] = onRemove;
    $[248] = removeDisabled;
    $[249] = t75;
  } else {
    t75 = $[249];
  }
  let t76;
  if ($[250] !== t57 || $[251] !== t72 || $[252] !== t73 || $[253] !== t75) {
    t76 = <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{t57}{t72}{t73}{t75}</div>;
    $[250] = t57;
    $[251] = t72;
    $[252] = t73;
    $[253] = t75;
    $[254] = t76;
  } else {
    t76 = $[254];
  }
  let t77;
  if ($[255] !== t47 || $[256] !== t76) {
    t77 = <div className="flex w-full min-w-0 items-start gap-2">{t47}{t76}</div>;
    $[255] = t47;
    $[256] = t76;
    $[257] = t77;
  } else {
    t77 = $[257];
  }
  return t77;
}
function _temp3(current_0) {
  return current_0 === "value" ? null : current_0;
}
function _temp2(current) {
  return current === "value" ? "value" : null;
}
function _temp(target_0) {
  return target_0 instanceof Element && Boolean(target_0.closest("[data-filter-value-trigger]"));
}